import React, { useCallback, useMemo } from "react";
import {
  useAllRoyaltiesByPublisher,
  useAllRoyaltiesByType,
  useAllRoyaltiesForYear,
  useAllRoyaltiesForPublisher,
  useUserCurrencyFormatter,
} from "@seabrookstudios/pitch2table";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot } from "@mui/x-charts/LineChart";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { royaltyToYear, useTallyByType, useTallyByYear } from "@seabrookstudios/pitch2table-core";
import { ParetoChart } from "../core/ParetoChart";
import { usePublisherNames } from "../publisher/queries";
import { useGameNames } from "../games/queries";
import { useAllRoyaltiesUnitsByYear } from "./queries";
import { useYearFromParam } from "./params";

/**
 * Format numbers like 1K, 10K, 1M.
 * @param {number|null|undefined} value
 */
const formatCompactNumber = (value) => {
  if (value === null || value === undefined) return "";
  if (!Number.isFinite(value)) return "";

  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (abs >= 1_000_000_000) return `${sign}${Math.round(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${sign}${Math.round(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}${Math.round(abs / 1_000)}K`;
  return `${value}`;
};

export const RoyaltyByTypeChart = () => {
  const { data: byType = [], isLoading } = useAllRoyaltiesByType();
  const data = useTallyByType(byType);

  return (
    <Box>
      <PieChart
        loading={isLoading}
        skipAnimation
        series={[
          {
            data,
          },
        ]}
        margin={{ bottom: 50, right: 0, left: 0, top: 0 }}
        slotProps={{
          legend: {
            direction: "horizontal",
            position: { vertical: "bottom", horizontal: "center" },
          },
        }}
        height={200}
      />
      <Divider />
    </Box>
  );
};

export const RoyaltyByYearChart = () => {
  const { data: byYear = [], unitsByYear, isLoading } = useAllRoyaltiesUnitsByYear();
  const data = useTallyByYear(byYear);
  const formatUserCurrency = useUserCurrencyFormatter();

  const BarChartC = useCallback(() => {
    const axisData = byYear.map((royalties) => royaltyToYear(royalties[0]));

    if (isLoading) {
      return (
        <Box height={200} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <ChartContainer
        height={200}
        series={[
          {
            type: "bar",
            data,
            yAxisId: "royalties",
            label: "Royalties",
            valueFormatter: (v) => formatUserCurrency(v),
          },
          {
            type: "line",
            data: unitsByYear,
            yAxisId: "units",
            label: "Units",
          },
        ]}
        xAxis={[
          {
            scaleType: "band",
            data: axisData,
          },
        ]}
        yAxis={[
          {
            id: "royalties",
            position: "left",
            valueFormatter: (v) => formatCompactNumber(v),
          },
          {
            id: "units",
            position: "right",
            label: "Units",
            valueFormatter: (v) => formatCompactNumber(v),
          },
        ]}
      >
        <BarPlot />
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="royalties" />
        <ChartsYAxis axisId="units" />
        <ChartsTooltip />
      </ChartContainer>
    );
  }, [byYear, data, unitsByYear]);

  return (
    <Box>
      <BarChartC />
      <Divider />
    </Box>
  );
};

/**
 * Royalties for a single publisher, broken down by year with units.
 *
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherId|undefined} props.publisherId
 */
export const RoyaltiesByYearForPublisherChart = ({ publisherId }) => {
  const { data: royalties = [], isLoading } = useAllRoyaltiesForPublisher(
    publisherId === undefined || publisherId === "" ? "unlinked" : publisherId
  );
  const formatUserCurrency = useUserCurrencyFormatter();

  const { axisYears, royaltiesByYear, unitsByYear } = useMemo(() => {
    if (!publisherId || publisherId === "") return { axisYears: [], royaltiesByYear: [], unitsByYear: [] };

    /** @type {Record<string, { total: number, units: number }>} */
    const byYear = {};

    for (const r of royalties) {
      const year = royaltyToYear(r);
      if (!year) continue;
      byYear[year] = byYear[year] || { total: 0, units: 0 };
      byYear[year].total += r.convertedAmount || 0;
      byYear[year].units += r.units || 0;
    }

    const axisYears = Object.keys(byYear).sort();
    const royaltiesByYear = axisYears.map((y) => byYear[y].total);
    const unitsByYear = axisYears.map((y) => byYear[y].units);

    return { axisYears, royaltiesByYear, unitsByYear };
  }, [royalties, publisherId]);

  const ChartC = useCallback(() => {
    if (isLoading) {
      return (
        <Box height={200} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (axisYears.length === 0) {
      return null;
    }

    return (
      <ChartContainer
        height={200}
        series={[
          {
            type: "bar",
            data: royaltiesByYear,
            yAxisId: "royalties",
            label: "Royalties",
            valueFormatter: (v) => formatUserCurrency(v),
          },
          {
            type: "line",
            data: unitsByYear,
            yAxisId: "units",
            label: "Units",
          },
        ]}
        xAxis={[{ scaleType: "band", data: axisYears }]}
        yAxis={[
          { id: "royalties", position: "left", valueFormatter: (v) => formatCompactNumber(v) },
          { id: "units", position: "right", label: "Units", valueFormatter: (v) => formatCompactNumber(v) },
        ]}
      >
        <BarPlot />
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="royalties" />
        <ChartsYAxis axisId="units" />
        <ChartsTooltip />
      </ChartContainer>
    );
  }, [axisYears, isLoading, royaltiesByYear, unitsByYear, formatUserCurrency]);

  return (
    <Box>
      <ChartC />
      <Divider />
    </Box>
  );
};

const PARETO_THRESHOLD = 10;

/**
 * Royalties for a single year, broken down by game (Pareto).
 * Filters royalty data by year, groups by game, and renders a generic ParetoChart
 * with threshold so 10+ games are collapsed into one column.
 */
export const RoyaltiesByYearParetoChart = () => {
  const year = useYearFromParam();
  const { data: royalties = [], isLoading } = useAllRoyaltiesForYear(year);

  const gameIdsByTotal = useMemo(() => {
    const byGame = /** @type {Record<string, number>} */ ({});
    for (const r of royalties) {
      const id = r.gameId ?? "unset";
      byGame[id] = (byGame[id] ?? 0) + (r.convertedAmount ?? 0);
    }
    return Object.entries(byGame)
      .map(([gameId, total]) => ({ gameId, total }))
      .sort((a, b) => b.total - a.total);
  }, [royalties]);

  const gameIds = useMemo(
    () => gameIdsByTotal.map(({ gameId }) => gameId).filter((id) => id !== "unset").slice(0, PARETO_THRESHOLD),
    [gameIdsByTotal]
  );
  const names = useGameNames(/** @type {import("@seabrookstudios/types").GameId[]} */ (gameIds));
  const nameByGameId = useMemo(
    () => Object.fromEntries(gameIds.map((id, i) => [id, names[i] ?? id])),
    [gameIds, names]
  );

  const items = useMemo(() => {
    return gameIdsByTotal.map(({ gameId, total }) => ({
      id: gameId,
      label: gameId === "unset" ? "Unknown game" : nameByGameId[gameId] ?? gameId,
      value: total,
    }));
  }, [gameIdsByTotal, nameByGameId]);

  const formatUserCurrency = useUserCurrencyFormatter();

  if (!year) return null;

  return (
    <Box>
      <ParetoChart
        items={items}
        threshold={PARETO_THRESHOLD}
        valueFormatter={formatUserCurrency}
        height={280}
        isLoading={isLoading}
      />
      <Divider />
    </Box>
  );
};

const PARETO_PUBLISHER_THRESHOLD = 10;

/**
 * Royalties grouped by publisher (Pareto).
 * Uses all royalties grouped by publisher; top publishers as columns, 10+ collapsed into "10+ publishers".
 */
export const RoyaltiesByPublisherParetoChart = () => {
  const { data: byPublisher = [], isLoading } = useAllRoyaltiesByPublisher();

  const publisherIdsByTotal = useMemo(() => {
    return byPublisher
      .map((royalties) => {
        const publisherId = royalties[0]?.publisherId ?? "unset";
        const total = royalties.reduce((sum, r) => sum + (r.convertedAmount ?? 0), 0);
        return { publisherId, total };
      })
      .sort((a, b) => b.total - a.total);
  }, [byPublisher]);

  const publisherIdsForNames = useMemo(
    () => publisherIdsByTotal.map(({ publisherId }) => publisherId).slice(0, 10),
    [publisherIdsByTotal]
  );
  const names = usePublisherNames(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId[]} */ (publisherIdsForNames)
  );
  const nameByPublisherId = useMemo(
    () => Object.fromEntries(publisherIdsForNames.map((id, i) => [id, names[i] || (id === "unset" ? "Unlinked" : id)])),
    [publisherIdsForNames, names]
  );

  const items = useMemo(() => {
    return publisherIdsByTotal.map(({ publisherId, total }) => ({
      id: publisherId ?? "unset",
      label: publisherId === "unset" || !publisherId ? "Unlinked" : nameByPublisherId[publisherId] || publisherId,
      value: total,
    }));
  }, [publisherIdsByTotal, nameByPublisherId]);

  const formatUserCurrency = useUserCurrencyFormatter();

  return (
    <Box>
      <ParetoChart
        items={items}
        threshold={PARETO_PUBLISHER_THRESHOLD}
        collapseLabel="10+ publishers"
        valueFormatter={formatUserCurrency}
        height={280}
        isLoading={isLoading}
      />
      <Divider />
    </Box>
  );
};
